/**
 * Injected scope configuration (`SCOPE_CONFIG`) — the build-time-ratified
 * category subsets the 04-03 scope engine needs but must NOT hard-code.
 *
 * The scope engine (`src/lib/scope.ts`) takes a `ScopeConfig` parameter so the
 * Finance-relevant / worker-safety category subsets (and a future native
 * `FinanceRelevant` flag) flow in from ONE place without editing the pure
 * engine. This file is that one place.
 *
 * Source of the subsets: `04-04-rbac-resolved.md` §4 — the PROPOSED subsets
 * drawn verbatim from the actual choice sets (Incidents `Category` = 31 values,
 * Complaints `Category` = 13 values). These are the build DEFAULTS pending
 * ratification with Amanda (Finance, §4a) and Amran (HR/WHS, §4b); until then
 * they stand (04-CONTEXT.md §8 — defaults, not invented values).
 *
 * REUSE: 04-05 (tiles), 04-07 (register views), and 04-08 (closure trail) all
 * import `SCOPE_CONFIG` and pass it to `applyScope` so every surface scopes
 * identically. Inject this — never re-declare the subsets at a call site.
 *
 * NOTE on the data posture (04-CONTEXT.md §3): scope filtering here is
 * PRESENTATION UX over a delegated-Graph response, not a hard confidentiality
 * boundary. Widening or narrowing a subset changes what a role sees by default,
 * not what it could reach in devtools — the honest v1 posture harmony-ap took.
 */

import type { ScopeConfig } from '../lib/scope';

/**
 * Finance-relevant `Category` values (04-04 §4a). Approximates the
 * `Finance-relevant` scope token until a native `FinanceRelevant` flag lands.
 * Spans Incidents + Complaints categories (the engine matches a record's
 * `category` against this flat set regardless of register).
 *
 * CONFIRM (04-04 §4a): ratify with Amanda; the `Service quality - Equipment`
 * (Complaints) borderline is currently EXCLUDED (only finance-relevant when it
 * implies a billing/cost dispute) — add it here if Amanda rules it in.
 */
const FINANCE_CATEGORIES: readonly string[] = [
  // Incidents (01-02 schema)
  'Abuse - Financial',
  'Other - Fraud',
  'Other - Property loss, damage, malfunction',
  'Other - Business interruption',
  // Complaints (01-05 schema)
  'Pricing and billing',
] as const;

/**
 * Worker-safety / HR-WHS `Category` values (04-04 §4b). SUPPLEMENTS the primary
 * `businessUnit == 'Staff'` signal (handled in the engine) so client-BU items
 * with a staff-safety angle (e.g. occupational violence by a client toward a
 * worker) still surface for HRWHS.
 *
 * CONFIRM (04-04 §4b): ratify with Amran; the other `Illness/injury - *` values
 * are currently EXCLUDED (better captured by `businessUnit == 'Staff'` than by
 * category) — add specific ones here if Amran rules them in.
 */
const WORKER_SAFETY_CATEGORIES: readonly string[] = [
  // Incidents (01-02 schema)
  'Abuse - Physical assault/aggression',
  'Abuse - Psychological, Emotional',
  'Abuse - Sexual',
  'Abuse - Other inappropriate behaviour',
  'Other - Hazard, Near miss',
  'Other - Chemical/Biological spill, hazard',
  'Illness/injury - Psychological, Emotional',
  // Complaints (01-05 schema)
  'Employee conduct',
] as const;

/**
 * The single injected `ScopeConfig` every dashboard surface passes to
 * `applyScope`. Named clearly for cross-plan reuse (04-05 / 04-07 / 04-08).
 */
export const SCOPE_CONFIG: ScopeConfig = {
  financeCategories: FINANCE_CATEGORIES,
  workerSafetyCategories: WORKER_SAFETY_CATEGORIES,
};
